import { Link } from "@tanstack/react-router";
import { DEFAULT_CATALOG_SEARCH } from "../lib/types";
import { Masthead } from "../components/Masthead";
import "../styles/about.css";

// "How it was rebuilt" — the long-form recovery story. Ported from the standalone
// prototype into a real route so it shares the catalog's design system and the
// header links to it. All styling lives in about.css, scoped under .about-page.

// Inline CSS custom properties (--w track fill) aren't in the React.CSSProperties
// type; this cast keeps them type-checked at the call site.
const vars = (v: Record<string, string>) => v as React.CSSProperties;

export function AboutPage() {
  return (
    <div className="about-page">
      <a className="skip" href="#main">
        Skip to content
      </a>

      <Masthead variant="about" />

      <main id="main">
        <section className="hero wrap" id="top">
          <h1>Rebuilding 2000s-era web games from half a million archive captures.</h1>
          <p className="lede">
            reArcade runs these games exactly as they shipped: the <b>original</b> Flash and Java
            binaries, dug out of public archives, validated byte by byte, and ported to run on the modern
            web.
          </p>
        </section>

        <section className="block wrap" id="dig">
          <h2>Start by finding everything that ever existed</h2>
          <p className="body narrow">
            Recovery begins with the Internet Archive's CDX index: every capture of every file under
            every game directory, across every mirror, across every year. For FreeArcade and a handful of
            smaller portals that came to <b>516,690 captures</b>, most of them the same files saved over
            and over. Each is deduped by content digest, ranked best first, and checked against its file
            signature, so only valid, distinct binaries are promoted.
          </p>

          <div className="sig filepile" aria-label="Poorly named recovered files">
            <p className="hint">
              Most archive pulls arrived with abbreviated or obfuscated names, so figuring out which
              files belonged together became its own recovery job:
            </p>
            <div className="filedir" role="group" aria-label="Recovered Yahoo file directory">
              <div className="filedir-bar" aria-hidden="true">
                <span className="filedir-path">/archive/yahoo/recovered/</span>
              </div>
              <ul className="filedir-list" tabIndex={0}>
                <li>pote_x.cab</li>
                <li>ct2_x.cab</li>
                <li>dot7_x.cab</li>
                <li>tt3_x.cab</li>
                <li>yt1_x.cab</li>
                <li>yog61.games.scd.yahoo.com/yog/y/ywt0_x.cab</li>
                <li>cw.jar</li>
                <li>games.yahoo.com/litsoft/crossword/y/cw/0.class</li>
                <li>games.yahoo.com/litsoft/crossword/y/cw/8.class</li>
                <li>mz.jar</li>
                <li>ca.games.yahoo.com/litsoft/general/mz.jar</li>
                <li>ar.games.yahoo.com/litsoft/general/mz.jar</li>
                <li>y/ar2.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/as0.cab</li>
                <li>yog8.yahoo.com/yog/y/as0_x.cab</li>
                <li>y/blr1.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/blr1.cab</li>
                <li>y/ks0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/ks0.cab</li>
                <li>y/cr1.jar</li>
                <li>yog8.yahoo.com/yog/y/ck6_x.cab</li>
                <li>yog15.games.snv.yahoo.com/yog/y/cr1.cab</li>
                <li>y/ir2.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/ir2.cab</li>
                <li>y/is0.jar</li>
                <li>yog8.games.scd.yahoo.com/yog/y/is0.cab</li>
                <li>y/nr0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/nr0.cab</li>
                <li>y/gk4.jar</li>
                <li>yog8.yahoo.com/yog/y/gk4_x.cab</li>
                <li>y/zt0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/zt0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/zs0.cab</li>
                <li>y/hq0.jar</li>
                <li>yog8.yahoo.com/yog/y/hq0_x.cab</li>
                <li>y/ts0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/ts0.cab</li>
                <li>y/plq29.jar</li>
                <li>yog8.yahoo.com/yog/y/plq29_x.cab</li>
                <li>yog8.yahoo.com/yog/y/plq47_x.cab</li>
                <li>y/ywt0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/ywt0.jar</li>
                <li>y/ws0.jar</li>
                <li>yog15.games.snv.yahoo.com/yog/y/ws0.cab</li>
                <li>zt0.jar</li>
                <li>yog/y/z/us-t0.ldict</li>
                <li>i/us/ga/sx/gf.gif</li>
                <li>ywt0.jar</li>
                <li>yog/y/yw/us-t0.ldict</li>
                <li>i/us/ga/sx/yt.gif</li>
                <li>as0.jar</li>
                <li>yog/y/a/us-s0.ldict</li>
                <li>i/us/ga/sx/bg.gif</li>
                <li>gk4_x.jar</li>
                <li>yog/y/g/us-k4.ldict</li>
                <li>i/us/ga/sx/go.gif</li>
                <li>plq47_x.jar</li>
                <li>yog/y/pl/us-q47.ldict</li>
                <li>i/us/ga/sx/pl.gif</li>
                <li>ks0.jar</li>
                <li>yog/y/k/us-s0.ldict</li>
                <li>i/us/ga/sx/ck.gif</li>
                <li>cr1.jar</li>
                <li>yog/y/c/us-r1.ldict</li>
                <li>i/us/ga/sx/ch.gif</li>
                <li>blr1.jar</li>
                <li>yog/y/bl/us-r1.ldict</li>
                <li>i/us/ga/sx/bl.gif</li>
                <li>ir2.jar</li>
                <li>yog/y/i/us-r2.ldict</li>
                <li>i/us/ga/sx/cr.gif</li>
                <li>ts0.jar</li>
                <li>yog/y/t/us-s0.ldict</li>
                <li>i/us/ga/sx/lt.gif</li>
                <li>ws0.jar</li>
                <li>yog/y/w/us-s0.ldict</li>
                <li>i/us/ga/sx/ww.gif</li>
                <li>nt1_x.jar</li>
                <li>yog/y/n/us-t1.ldict</li>
                <li>i/us/ga/sx/gi.gif</li>
                <li>dot8_x.jar</li>
                <li>yog/y/do/us-t8.ldict</li>
                <li>i/us/ga/sx/do.gif</li>
                <li>dtq0_x.jar</li>
                <li>yog/y/dt/us-q0.ldict</li>
                <li>i/us/ga/sx/dt.gif</li>
                <li>fs10_x.jar</li>
                <li>yog/y/fs/us-10.ldict</li>
                <li>i/us/ga/sx/fs.gif</li>
                <li>grs0.jar</li>
                <li>yog/y/gr/us-s0.ldict</li>
                <li>i/us/ga/sx/gr.gif</li>
                <li>jt0.jar</li>
                <li>yog/y/j/us-t0.ldict</li>
                <li>i/us/ga/sx/bj.gif</li>
                <li>pot0.jar</li>
                <li>yog/y/po/us-t0.ldict</li>
                <li>pyt0.jar</li>
                <li>yog/y/py/us-t0.ldict</li>
              </ul>
            </div>
          </div>

          <section className="names-panel" id="names">
            <h2>Correlation from a stranger's virus log</h2>
            <p className="body narrow">
              Yahoo shipped its games as opaque archives: <code>pote_x.cab</code>, <code>ct2_x.cab</code>,
              <code>yt1_x.cab</code>, all dumped in one directory. The room pages should have been the
              lookup table, but the archived copies often stop at broken wrappers or redirects.
            </p>
            <p className="body narrow">
              That list survived in about the least likely place on the internet. In 2005, people whose
              browsers had been hijacked by spyware pasted diagnostic logs onto tech-support forums to beg
              for help. The logs printed every game applet the machine had ever cached, by name, next to
              the exact file it was downloaded from.
            </p>

            <figure className="logdir-figure">
              <div className="filedir logdir" role="group" aria-label="Virus log correlation excerpt">
                <div className="filedir-bar" aria-hidden="true">
                  <span className="filedir-path">hijacked PC log / downloaded program files</span>
                </div>
                <ul className="filedir-list" tabIndex={0}>
                  <li className="ellipsis">...</li>
                  <li>
                    <span className="op">O16 - DPF:</span> <span className="title">Yahoo! Pool 2</span> -
                    http://download.games.yahoo.com/games/clients/y/
                    <span className="cab" data-cab="pote">pote_x.cab</span>
                  </li>
                  <li>
                    <span className="op">O16 - DPF:</span> <span className="title">Yahoo! Chess</span> -
                    http://download.games.yahoo.com/games/clients/y/
                    <span className="cab" data-cab="ct2">ct2_x.cab</span>
                  </li>
                  <li>
                    <span className="op">O16 - DPF:</span> <span className="title">Yahoo! Dominoes</span> -
                    http://download.games.yahoo.com/games/clients/y/
                    <span className="cab" data-cab="dot7">dot7_x.cab</span>
                  </li>
                  <li>
                    <span className="op">O16 - DPF:</span> <span className="title">Yahoo! Literati</span> -
                    http://download.games.yahoo.com/games/clients/y/
                    <span className="cab" data-cab="tt3">tt3_x.cab</span>
                  </li>
                  <li>
                    <span className="op">O16 - DPF:</span> <span className="title">Yahoo! Canasta</span> -
                    http://download.games.yahoo.com/games/clients/y/
                    <span className="cab" data-cab="yt1">yt1_x.cab</span>
                  </li>
                  <li className="ellipsis">...</li>
                </ul>
              </div>

              <p className="caption">
                Verbatim from a 2005 thread on Dell's support forum. That one log help map 14 Yahoo games
                and 10 from Pogo.
              </p>
            </figure>
          </section>

          <section className="names-panel" id="asset-loop">
            <h2>This was only the beginning</h2>
            <p className="body narrow">
              A title did not make the game run, but it changed what the archive search could ask for. Once
              <code>ywt0.jar</code> became Yahoo Towers, the missing pieces had names to hunt: string
              tables, button sheets, icons, room chrome, table art, and language dictionaries.
            </p>
            <p className="body narrow">
              Many recovered clients opened to a blank or half-drawn lobby. The code was there, but the
              client kept requesting resources that had not been paired with the game yet.
            </p>

            <figure className="asset-loop-figure">
              <div className="shots compare">
                <div className="shot warn towers-room">
                  <div className="frame">
                    <img
                      src="/assets/about/yahoo-towers-room-recovered.png"
                      alt="Yahoo Towers room before original assets were located, with placeholder X labels and missing Yahoo branding"
                      loading="eager"
                    />
                  </div>
                  <span className="status">before: missing assets</span>
                </div>
                <div className="shot ok towers-assets">
                  <div className="frame">
                    <img
                      src="/assets/about/yahoo-towers-original-assets.png"
                      alt="Yahoo Towers room after original assets were located, showing Yahoo Games branding, restored labels, item icons, controls, chat, and player boards"
                      loading="eager"
                    />
                  </div>
                  <span className="status">after: original assets located</span>
                </div>
              </div>
              <p className="caption">
                The broken lobby was still useful: it proved the applet could start, then showed exactly
                which labels, icons, buttons, and sounds were still missing.
              </p>
            </figure>

            <p className="body narrow" style={vars({ marginTop: "var(--space-6)" })}>
              The rest of recovery became a loop: launch the original client, record every missing request
              and placeholder lookup, crawl every archived mirror and year for that path or its siblings,
              drop the file into the local mirror, then launch the client again. Each pass brought more of
              the game back until every missing asset was resolved.
            </p>
          </section>

          <section className="names-panel" id="yog-server">
            <h2>Rebuilding a lost server</h2>
            <p className="body narrow">
              With the assets back, the next missing piece was the backend. With the servers gone, the
              only insight we had was the clients still reaching for lobbies, match servers, and
              leaderboards that stopped responding a long time ago.
            </p>
            <p className="body narrow">
              Each backend is rebuilt by working backward from the client. Decompiling it gives back its
              objects, but with every name stripped to noise: a button is just a number, its label a
              pointer into a string table we had to recover. Read an object's properties, see the byte its
              handler emits, and the server's job follows. Below is Yahoo Towers' start-of-game handshake,
              recovered that way.
            </p>
            <div className="story">
              <div className="story-head">
                <span className="hc">Decompiled objects</span>
                <span className="hs">Inferred server · towers.mjs</span>
              </div>

              <div className="step">
                <div className="duo">
                  <div className="cli">
                    <span className="ent">btn #42</span>
                    <span className="prop">
                      .label = ldict[0x1a]<span className="cmt">// "Ready"</span>
                    </span>
                    <span className="prop">
                      .onClick = send(<span className="bx">0x44 'D'</span>)
                    </span>
                  </div>
                  <div className="srv">
                    <span className="ln">
                      <span className="pl">+</span> on(<span className="bx">0x44</span>):
                    </span>
                    <span className="ln">
                      <span className="pl">+</span> seat.ready = <span className="kw">true</span>
                    </span>
                    <span className="ln">
                      <span className="pl">+</span> <span className="kw">if</span> (allReady) broadcast(
                      <span className="bx">0x39</span>)
                    </span>
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="duo">
                  <div className="cli">
                    <span className="ent">btn #17</span>
                    <span className="prop">
                      .label = ldict[0x33]<span className="cmt">// "Start"</span>
                    </span>
                    <span className="prop">
                      .enabledOn = recv(<span className="bx">0x39 '9'</span>)
                    </span>
                    <span className="prop">
                      .onClick = send(<span className="bx">0x42 'B'</span>)
                    </span>
                  </div>
                  <div className="srv">
                    <span className="ln">
                      <span className="pl">+</span> on(<span className="bx">0x42</span>):
                    </span>
                    <span className="ln">
                      <span className="pl">+</span> broadcast(<span className="bx">0x62</span>)
                    </span>
                  </div>
                </div>
              </div>

              <div className="step">
                <div className="duo">
                  <div className="cli">
                    <span className="ent">game</span>
                    <span className="prop">
                      .startsOn = recv(<span className="bx">0x62 'b'</span>)
                    </span>
                  </div>
                  <div className="srv">
                    <span className="ln">
                      <span className="cmt-s">// continue this pattern until we have a fully functional game</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </section>

        <section className="block wrap" id="held">
          <h2>What's still in the queue</h2>
          <p className="body narrow">
            277 titles are recovered but not yet playable.
          </p>

          <div className="queue" aria-label="Held games by reason">
            <div className="qrow" style={vars({ "--w": "100%" })}>
              <div className="qtop">
                <span className="qlabel">
                  GameJacket wrappers <small>need a GameJacket server that no longer exists</small>
                </span>
                <span className="qn">134</span>
              </div>
              <div className="track">
                <i></i>
              </div>
            </div>
            <div className="qrow" style={vars({ "--w": "52%" })}>
              <div className="qtop">
                <span className="qlabel">
                  Blank stage or runtime error <small>fail to render in Ruffle or CheerpJ</small>
                </span>
                <span className="qn">~70</span>
              </div>
              <div className="track">
                <i></i>
              </div>
            </div>
            <div className="qrow" style={vars({ "--w": "27%" })}>
              <div className="qtop">
                <span className="qlabel">
                  Self-contained Yahoo applets <small>recovered, waiting on a runtime test</small>
                </span>
                <span className="qn">36</span>
              </div>
              <div className="track">
                <i></i>
              </div>
            </div>
            <div className="qrow" style={vars({ "--w": "10%" })}>
              <div className="qtop">
                <span className="qlabel">
                  Yahoo server-dependent <small>bytecode kept for a future server port</small>
                </span>
                <span className="qn">13</span>
              </div>
              <div className="track">
                <i></i>
              </div>
            </div>
          </div>
          <p className="queue-foot">
            Each fix moves a title off this list and into the catalog.
          </p>
        </section>

        <section className="close wrap">
          <p className="ethos">
            The original games, playable again.
          </p>
          <Link className="home" to="/" search={DEFAULT_CATALOG_SEARCH}>
            Browse the catalog
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
        </section>
      </main>
    </div>
  );
}
